import { PrivacyPolicy, SubmittedPrivacyPolicy } from '@teko/privacy-policy';
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useServices } from './ServiceContext';
import { logger } from '@teko/log';
import {
  BottomActionBar,
  BottomSheet,
  Button,
  Checkbox,
  Shape,
  ThemeProps,
  styled,
  Modal,
  ModalBody,
  useModal,
  Body,
} from '@teko/ds-mobile';
import { t } from 'i18n';
import { InforOutlined } from '@teko/ds-icon';
import { useTenantConfig } from './GlobalDataContext';
import { useAuth } from '@teko/auth-react';
import {
  LOCAL_STORAGE_PRIVACY_POLICIES,
  LOCAL_STORAGE_SUBMIT_PRIVACY_POLICIES,
  LOCAL_STORAGE_ALLOWED_PRIVACY_POLICY,
} from '../constants';
import { parseJsonStringSafely } from '@teko/utils';
import { useRouter } from 'next/router';
import { useFeatureFlags, useGlobalData } from 'contexts';
import { isBankPartner, onSignOut } from 'helpers';
import { PolicyTypeId } from 'types';
import { getTrackingData, trackManually } from '@teko/tracking';

export interface SelectablePolicy extends PrivacyPolicy {
  isApproved?: boolean;
}

export interface PrivacyPolicyContext {
  loading: boolean;
  policies: SelectablePolicy[];
  shouldShowSelector: boolean;
  changePolicyApproval: (id: number, state: boolean) => void;
  showSelector: (callback: Function, isMobile?: boolean) => void;
  submitPoliciesApproval: (callback?: Function) => Promise<void>;
  buildPrivacyPolicyList: () => ReactNode;
}

interface LocalStorageSubmitPolicies {
  policies: SubmittedPrivacyPolicy[];
  isSubmitted: boolean;
  createdAt: string;
}

interface LocalStoragePolicies {
  policies?: SelectablePolicy[];
  createdAt: string;
}

interface LocalStorageAllowedStatus {
  isAllowed: boolean;
  createdAt: string;
}

const initialValues = {
  loading: false,
  policies: [],
  shouldShowSelector: false,
  changePolicyApproval: () => {},
  showSelector: () => {},
  submitPoliciesApproval: async () => {},
  buildPrivacyPolicyList: () => <></>,
};

const PrivacyPolicyContext = createContext<PrivacyPolicyContext>(initialValues);

// currently cached submit payload is hardcoded to expire after 3 days.
const submitPayloadTTL = 3;

export const PrivacyPolicyProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const router = useRouter();
  const { user, loading: authLoading, authService } = useAuth();
  const { privacyPolicyService, notificationService } = useServices();
  const { showModal } = useModal();
  const { flags } = useFeatureFlags();
  const {
    privacyPolicyCacheTTL = 30,
    isMobile,
    currentLanguage,
    notificationAppId,
  } = useTenantConfig();
  const { partner } = useGlobalData();

  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [policies, setPolicies] = useState<SelectablePolicy[]>([]);
  const [shouldShowSelector, setShouldShowSelector] = useState(false);
  const submitCallback = useRef<Function>(() => {});

  const isLoginPage = !!router.pathname.endsWith('/login');

  const submitPoliciesApproval = async (callback?: Function) => {
    if (validateApproval(policies)) {
      try {
        setLoading(true);
        await internalSubmit(authService.isLoggedIn());
      } catch (e) {
        // submit failed, cache saved for retry later so no need to do anything
      } finally {
        setShow(false);
        setLoading(false);
        callback?.();
      }
    } else {
      showWarningModal();
    }
  };

  const changePolicyApproval = useCallback(
    (id: number, state: boolean) => {
      const newPolicies = policies.map((policy) => {
        if (policy.id === id) {
          trackManually(
            state ? 'checkPrivacy' : 'uncheckPrivacy',
            'privacyContent',
            '',
            JSON.stringify({
              screenName: authService.isLoggedIn()
                ? 'userPrivacyPopup'
                : 'loginMethod',
              policy: policy.description,
            }),
          );
          return { ...policy, isApproved: state };
        } else return policy;
      });
      setPolicies(newPolicies);
    },
    [policies],
  );

  /**
   * Underlying logic to handle fetch policies from api and save to cache
   * */
  const internalGetPolicies = useCallback(
    async (isAuthed?: boolean) => {
      const policies = await privacyPolicyService.getPolicies();
      const isUserAllowed = checkUserAllowed(policies);
      const now = new Date().toString();

      setPolicies(policies);
      setShouldShowSelector(policies.length > 0);

      // Dont cache user specified policies
      if (!isAuthed) {
        localStorage.setItem(
          `${LOCAL_STORAGE_PRIVACY_POLICIES}_${currentLanguage || 'vi'}`,
          JSON.stringify({
            policies: policies,
            createdAt: now,
          } as LocalStoragePolicies),
        );
      } else {
        localStorage.setItem(
          LOCAL_STORAGE_ALLOWED_PRIVACY_POLICY,
          JSON.stringify({
            isAllowed: isUserAllowed,
            createdAt: now,
          } as LocalStorageAllowedStatus),
        );
      }
    },
    [currentLanguage, privacyPolicyService],
  );

  /**
   * Underlying logic to submit approval to remote api and handle error
   * */
  const internalSubmit = useCallback(
    async (
      isAuthed: boolean,
      customPolicies?: SubmittedPrivacyPolicy[],
      skipSave = false,
    ) => {
      // policy type 1 is only used to show to user
      const submitPolicies: SubmittedPrivacyPolicy[] =
        customPolicies ||
        policies
          .filter((policy) => policy.policyTypeId !== PolicyTypeId.TYPE_1)
          .map(
            ({ id, version, language, isApproved }) =>
              ({
                id,
                version,
                language,
                isApproved: isApproved || false,
              } as SubmittedPrivacyPolicy),
          );
      try {
        // only submit when accessToken is available
        if (isAuthed) {
          await privacyPolicyService.submitPolicies(submitPolicies);
          localStorage.removeItem(LOCAL_STORAGE_SUBMIT_PRIVACY_POLICIES);
        } else !skipSave && saveSubmitPolicies(submitPolicies);
      } catch (e) {
        logger.error(e);
        // saved for retry later
        if (!skipSave) saveSubmitPolicies(submitPolicies);
        throw e;
      }
    },
    [policies, privacyPolicyService],
  );

  /**
   * Submit if payload cached
   * @param {boolean} isAuthed - authenticate status
   * @param {LocalStorageSubmitPolicies} [savedSubmitPolicies] - saved submit payload in cached
   * */
  const validateSubmitPolicies = useCallback(
    async (
      isAuthed: boolean,
      savedSubmitPolicies?: LocalStorageSubmitPolicies,
    ) => {
      if (isAuthed && savedSubmitPolicies) {
        const { policies, isSubmitted } = savedSubmitPolicies;
        if (!!policies && isSubmitted === false) {
          await internalSubmit(isAuthed, savedSubmitPolicies.policies, true);
        }
      }
    },
    [internalSubmit],
  );

  /**
   * Decide to using cache or fetch from api depend on authenticate status and saved values
   * @param {boolean} isAuthed - authenticate status
   * @param {LocalStoragePolicies} [savedGetPolicies] - saved GetPolicies response.
   * @param {LocalStorageAllowedStatus} [savedAllowedStatus] - saved user allow status.
   * */
  const validatePolicies = useCallback(
    async (
      isAuthed: boolean,
      savedGetPolicies?: LocalStoragePolicies,
      savedAllowedStatus?: LocalStorageAllowedStatus,
    ) => {
      try {
        if (!isAuthed) {
          if (savedGetPolicies) {
            const savedPolicies = savedGetPolicies.policies || [];
            setPolicies(savedPolicies);
            setShouldShowSelector(savedPolicies.length > 0);
          } else {
            internalGetPolicies();
          }
        } else if (
          !savedAllowedStatus ||
          (savedAllowedStatus && !savedAllowedStatus.isAllowed)
        ) {
          // fetch privacy policies specify to this user then skip cache step
          internalGetPolicies(true);
        }
      } catch (e) {
        // do nothing for now
      }
    },
    [internalGetPolicies],
  );

  const showSelector = useCallback(async (callback: Function) => {
    submitCallback.current = callback;
    setShow(true);
  }, []);

  /**
   * check for cache validation then remove it if expired and return it.
   * @return 3 saved value in form of array
   * */
  const validateCaches = () => {
    return (
      [
        [LOCAL_STORAGE_SUBMIT_PRIVACY_POLICIES, submitPayloadTTL],
        [
          `${LOCAL_STORAGE_PRIVACY_POLICIES}_${currentLanguage || 'vi'}`,
          privacyPolicyCacheTTL,
        ],
        [LOCAL_STORAGE_ALLOWED_PRIVACY_POLICY, privacyPolicyCacheTTL],
        // in form of [<ref object to save into>, <time to live in day>, <cache key>]
      ] as Array<[string, number]>
    ).map(([key, duration]) => {
      const value = parseJsonStringSafely(localStorage.getItem(key)) as {
        createdAt: string;
      };
      if (!value || checkCacheExpired(value.createdAt, duration)) {
        localStorage.removeItem(key);
        return undefined;
      }
      return value;
    }) as [
      LocalStorageSubmitPolicies,
      LocalStoragePolicies,
      LocalStorageAllowedStatus,
    ];
  };

  /**
   * this effect only run once auth loaded.
   * 1. validate caches (return cached values if valid or remove otherwise).
   * 2. validate submit (submit to api if a submit payload saved which retrieved in previous step)
   * 3. if step 3 success (submitted or nothing to submit yet) then validate policies (decide using cache or fetch from api)
   * 4. app is not IBanking App
   * */
  useEffect(() => {
    const isIBankingApp = partner ? isBankPartner(partner) : false;
    if (!authLoading && flags.epic_ecom_483 && !isIBankingApp) {
      const isAuthed = authService.isLoggedIn();
      const [savedSubmitPolicies, savedPolicies, savedAllowedStatus] =
        validateCaches();
      validateSubmitPolicies(isAuthed, savedSubmitPolicies)
        .then(() =>
          validatePolicies(isAuthed, savedPolicies, savedAllowedStatus),
        )
        .catch(() => {}); // error handled
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, partner]);

  const showWarningModal = () => {
    showModal?.({
      title: t('Thông báo'),
      children: (
        <Body>
          {t(
            'Vui lòng xác nhận đồng ý với các điều khoản và điều kiện dịch vụ để tiếp tục',
          )}
        </Body>
      ),
      okText: t('Đóng'),
      cancelButtonProps: { style: { display: 'none' } },
      showCloseIcon: false,
      closable: false,
    });
  };

  const buildPrivacyPolicyList = useCallback(() => {
    return (
      <div
        style={{ textAlign: 'left' }}
        {...getTrackingData({
          isTrackContent: true,
          contentName: 'showPrivacPolicy',
          regionName: 'privacyContent',
          payload: JSON.stringify({
            screenName:
              typeof window !== 'undefined' && authService.isLoggedIn()
                ? 'userPrivacyPopup'
                : 'loginMethod',
            policies,
          }),
        })}
      >
        {policies.map((policy) => {
          // find the outermost html tag of in the string
          const HTMLTag =
            policy.description[0] === '<'
              ? policy.description
                  // start at 1 since we don't want to include '<'
                  .substring(1, policy.description.indexOf('>'))
                  // split by space in case html tag has attributes in it
                  .split(' ')
              : '';

          return policy.policyTypeId === PolicyTypeId.TYPE_1 ? (
            <WarningPolicyWrapper isMobile={isMobile} key={policy.id}>
              <InforOutlined
                size={24}
                color="pending600"
                style={{
                  flex: '0 0 auto',
                }}
              />
              {authService.isLoggedIn() ? (
                <div>
                  {t(
                    'Chúng tôi có các cập nhật liên quan đến việc xử lý dữ liệu cá nhân. Vui lòng kiểm tra và xác nhận. Các chính sách có đánh dấu * là bắt buộc xác nhận để tiếp tục sử dụng dịch vụ.',
                  )}
                </div>
              ) : (
                <HTMLContentContainer
                  dangerouslySetInnerHTML={{ __html: policy.description }}
                />
              )}
            </WarningPolicyWrapper>
          ) : (
            <SelectablePolicyWrapper key={policy.id}>
              <Checkbox
                height="min-content"
                checked={policy.isApproved}
                onChange={(state) => changePolicyApproval(policy.id, state)}
              />
              <HTMLContentContainer
                isRequire={policy.isApprovalRequired}
                HTMLTag={HTMLTag[0]} // first element in HTML tag will be the tag name
                dangerouslySetInnerHTML={{
                  __html: !!HTMLTag
                    ? policy.description
                    : `<div>${policy.description}</div>`,
                }}
              />
            </SelectablePolicyWrapper>
          );
        })}
      </div>
    );
  }, [authService, policies, isMobile, changePolicyApproval]);

  return (
    <PrivacyPolicyContext.Provider
      value={{
        loading,
        policies,
        shouldShowSelector,
        changePolicyApproval,
        showSelector,
        submitPoliciesApproval,
        buildPrivacyPolicyList,
      }}
    >
      {children}
      {isMobile ? (
        <BottomSheet
          show={show}
          title={t('Thông báo')}
          closeBottomSheet={isLoginPage ? () => setShow(false) : undefined}
        >
          <Shape
            width="100%"
            padding="s16"
            style={{
              marginBottom: '64px',
              width: '100%',
            }}
          >
            {buildPrivacyPolicyList()}
            <BottomActionBar
              style={{
                display: 'absolute',
                gap: '8px',
              }}
            >
              {!isLoginPage && (
                <Button
                  width="100%"
                  variant="secondary"
                  onClick={() => {
                    setShow(false);
                    setShouldShowSelector(false);
                    if (policies.some((policy) => policy.isApprovalRequired)) {
                      trackManually(
                        'logoutButton',
                        'privacyMethod',
                        'loginMethod',
                        JSON.stringify({ screenName: 'userPrivacyPopup' }),
                      );
                      onSignOut(
                        authService,
                        notificationService,
                        notificationAppId,
                      );
                    } else {
                      trackManually(
                        'quitButton',
                        'privacyMethod',
                        window.location.href,
                        JSON.stringify({ screenName: 'userPrivacyPopup' }),
                      );
                    }
                  }}
                >
                  {policies.some((policy) => policy.isApprovalRequired)
                    ? t('Đăng xuất')
                    : t('Bỏ qua')}
                </Button>
              )}
              <Button
                width="100%"
                variant="primary"
                onClick={() => {
                  trackManually(
                    'confirmButton',
                    'privacyMethod',
                    window.location.href,
                    JSON.stringify({
                      screenName: 'userPrivacyPopup',
                    }),
                  );
                  submitPoliciesApproval(submitCallback.current);
                }}
              >
                {t('Xác nhận')}
              </Button>
            </BottomActionBar>
          </Shape>
        </BottomSheet>
      ) : (
        <Modal
          width="600px"
          visible={show}
          showCloseIcon={false}
          closable={false}
          okText={t('Xác nhận')}
          onOk={() => {
            trackManually(
              'confirmButton',
              'privacyMethod',
              window.location.href,
              JSON.stringify({
                screenName: 'userPrivacyPopup',
              }),
            );
            submitPoliciesApproval();
          }}
          cancelText={t('Đăng xuất')}
          onCancel={() => {
            setShow(false);
            trackManually(
              'logoutButton',
              'privacyMethod',
              'loginMethod',
              JSON.stringify({ screenName: 'userPrivacyPopup' }),
            );
            onSignOut(authService, notificationService, notificationAppId);
          }}
          title={t('Thông báo')}
        >
          <ModalBody style={{ padding: 0 }}>
            {buildPrivacyPolicyList()}
          </ModalBody>
        </Modal>
      )}
    </PrivacyPolicyContext.Provider>
  );
};

export const usePrivacyPolicy = () => {
  const context = useContext(PrivacyPolicyContext);
  return context;
};

const validateApproval = (policies: SelectablePolicy[]) => {
  return !policies.some(
    (policy) => policy.isApprovalRequired && !policy.isApproved,
  );
};

const saveSubmitPolicies = (
  policies: SubmittedPrivacyPolicy[],
  customCreatedAt?: string,
) => {
  localStorage.setItem(
    LOCAL_STORAGE_SUBMIT_PRIVACY_POLICIES,
    JSON.stringify({
      policies: policies,
      isSubmitted: false,
      createdAt: customCreatedAt || new Date().toString(),
    } as LocalStorageSubmitPolicies),
  );
};

const checkCacheExpired = (date: string, duration: number) => {
  return !(
    (new Date().getTime() - new Date(date).getTime()) / (24 * 3600 * 1000) <=
    duration
  );
};

const checkUserAllowed = (policies: SelectablePolicy[]) =>
  !policies.some((policy) => policy.isApprovalRequired);

const WarningPolicyWrapper = styled.div(
  ({ theme, isMobile }: ThemeProps & { isMobile?: boolean }) => `
    padding: 8px 12px;
    background-color: ${theme?.colors.pending100};
    color: ${theme?.colors.pending600};
    display: flex;
    justify-content: start;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: ${isMobile ? '8px' : '16px'};
`,
);

const SelectablePolicyWrapper = styled.div`
  margin-bottom: 16px;
  display: flex;
  gap: 0.5rem;
`;

const HTMLContentContainer = styled.div(
  ({
    theme,
    isRequire,
    HTMLTag,
  }: ThemeProps & { isRequire?: boolean; HTMLTag?: string }) => `
  display: inline-block;
  & > p {
    margin: 0px;
  }
  ${
    isRequire
      ? !!HTMLTag
        ? `& > ${HTMLTag}:first-child::before {
        content: "*";
        margin-right: 0.5rem;
        color: ${theme?.colors.error500};
      }`
        : `& > div:first-child::before {
        content: "*";
        margin-right: 0.5rem;
        color: ${theme?.colors.error500};
      }`
      : ''
  }
`,
);
